import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

// One shared lock across all course purchase placements. Retrying always reads
// the cart first, including after an interrupted add request.
let pending = false;
const root = window.Shopify?.routes?.root || '/';
const sectionId = 'cart-drawer-section';
const drawer = () => document.getElementById('cart-drawer');
const track = (event, placement) => {
  window.dataLayer ||= [];
  window.dataLayer.push({ event, placement });
  window.gtag?.('event', event, { placement });
};
async function request(url, options = {}) {
  const response = await fetch(url, { ...options, headers: { Accept: 'application/json', ...options.headers }, signal: AbortSignal.timeout(15000) });
  const data = await response.json();
  if (!response.ok || data.status >= 400) throw new Error(data.description || 'Cart request failed');
  return data;
}
const getCart = () => request(`${root}cart.js`);
const updateCount = (cart) => document.querySelectorAll('[data-lm-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
async function renderDrawer(sections) {
  const html = sections?.[sectionId] || (await request(`${root}?sections=${sectionId}`))[sectionId];
  if (!html) throw new Error('Cart could not be displayed');
  await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
  await Promise.race([customElements.whenDefined('theme-drawer'), new Promise((_, reject) => setTimeout(() => reject(new Error('Cart is still loading')), 5000))]);
  if (!drawer()?.open) throw new Error('Cart is still loading');
  drawer().open();
  scheduleSticky();
}

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('[data-lm-course-form]');
  if (!form) return;
  event.preventDefault();
  if (pending) return;
  const button = form.querySelector('button[type="submit"]');
  if (button.disabled) return;
  const id = String(new FormData(form).get('id'));
  if (!id || id === 'null') return;
  pending = true;
  const buttons = [...document.querySelectorAll('[data-lm-course-form] button:not(:disabled)')];
  buttons.forEach((el) => { el.disabled = true; el.setAttribute('aria-busy', 'true'); });
  document.querySelectorAll('[data-lm-course-error]').forEach((el) => { el.hidden = true; });
  const error = form.querySelector('[data-lm-course-error]');
  const placement = form.dataset.placement;
  track('course_purchase_click', placement);
  let attemptedAdd = false;
  let deferred;
  let resolved = false;
  const resolve = (cart, sections) => {
    updateCount(cart);
    if (deferred && !resolved) {
      resolved = true;
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, itemCount: cart.item_count, sections, source: 'lm-course-form', sourceId: form.id, didError: false } });
    }
  };
  try {
    let cart = await getCart();
    let sections;
    const existing = cart.items.find((item) => String(item.variant_id) === id);
    if (!existing) {
      deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      form.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: id, quantity: 1 }], promise: deferred.promise }));
      const body = new FormData(form);
      body.set('quantity', '1');
      body.set('sections', sectionId);
      body.set('sections_url', window.location.pathname);
      attemptedAdd = true;
      const added = await request(`${root}cart/add.js`, { method: 'POST', body });
      sections = added.sections;
      cart = await getCart();
      resolve(cart, sections);
    } else {
      // A cart created before a price change can retain the old price until
      // recalculated. Refresh that line, keeping digital access at one unit.
      const expectedPrice = Number(form.dataset.lmVariantPrice);
      if (existing.quantity !== 1 || (Number.isFinite(expectedPrice) && expectedPrice > 0 && existing.price !== expectedPrice)) {
        cart = await request(`${root}cart/change.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: existing.key, quantity: 1, sections: sectionId, sections_url: window.location.pathname })
        });
        sections = cart.sections;
      }
      resolve(cart, sections);
    }
    await renderDrawer(sections);
  } catch (cause) {
    // The network may fail after Shopify accepts the add. Reconcile once;
    // never repeat the write automatically or silently fall back to a POST.
    let recovered = false;
    if (attemptedAdd) {
      try {
        const cart = await getCart();
        if (cart.items.some((item) => String(item.variant_id) === id)) {
          resolve(cart);
          await renderDrawer();
          recovered = true;
        }
      } catch { /* Show the same actionable error below. */ }
    }
    if (!recovered) {
      if (deferred && !resolved) { resolved = true; deferred.reject(cause); }
      error.textContent = 'We couldn’t open your cart. Please tap the button to retry. You won’t add a second course.';
      error.hidden = false;
      track('course_cart_error', placement);
    }
  } finally {
    pending = false;
    buttons.forEach((el) => { el.disabled = false; el.removeAttribute('aria-busy'); });
    scheduleSticky();
  }
});

document.addEventListener('click', async (event) => {
  const link = event.target.closest?.('.lm-icon-button--cart');
  if (!link || !document.querySelector('.lm-pdp') || !drawer()?.open) return;
  event.preventDefault();
  try { updateCount(await getCart()); await renderDrawer(); }
  catch { window.location.assign(link.href); }
});
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(() => getCart().then(updateCount)).catch(() => {});
});

let frame = 0;
function updateSticky() {
  frame = 0;
  const sticky = document.querySelector('[data-lm-course-sticky]');
  const hero = document.querySelector('[data-lm-course-hero-cta]');
  if (!sticky || !hero) return;
  const inView = (el) => {
    const rect = el?.getBoundingClientRect();
    return rect && rect.top < window.innerHeight + 90 && rect.bottom > 0;
  };
  const blocked = document.body.classList.contains('lm-menu-open') || drawer()?.hasAttribute('open') || inView(document.querySelector('[data-lm-course-final]')) || inView(document.querySelector('[data-lm-footer]'));
  // Keep buying available while browsing, without toggling at every inline CTA.
  const visible = hero.getBoundingClientRect().bottom < 0 && !blocked;
  sticky.classList.toggle('is-visible', visible);
  sticky.setAttribute('aria-hidden', String(!visible));
  sticky.inert = !visible;
}
function scheduleSticky() { if (!frame) frame = requestAnimationFrame(updateSticky); }
// Horizon's desktop drawer shell scrolls an inner page container; scroll does
// not bubble, so capture it as well as the ordinary mobile document scroll.
document.addEventListener('scroll', scheduleSticky, { passive: true, capture: true });
window.addEventListener('resize', scheduleSticky);
['theme-drawer:open', 'theme-drawer:close', 'shopify:section:load', 'shopify:section:reorder'].forEach((type) => document.addEventListener(type, scheduleSticky));
new MutationObserver(scheduleSticky).observe(document.body, { attributes: true, attributeFilter: ['class'] });
scheduleSticky();
